import { i18n } from "../../translations/i18n";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
    getErrorResponseToState,
    handleResponse,
    httpRequestInit,
    IFetchState,
    specialBody,
    specialRequestInitCaller,
    useFetchSpecialType,
} from "src/itsf/fetch/fetcherUtils";
import { TracingContext } from "src/itsf/contexts/TracingContext";

type specialRequestType = Pick<RequestInit, "headers"> & specialBody & useFetchSpecialType;

type getSpecialRequestType = Pick<RequestInit, "headers"> & useFetchSpecialType;

const useFetchGeneric = (genericErrorMessage: string, locale?: string) => {
    const didCancel = useRef(false);
    const { tracingFetcherWrapper } = useContext(TracingContext);
    const [state, setState] = useState<IFetchState>({ error: undefined, isLoading: false });

    useEffect(() => {
        return () => {
            didCancel.current = true;
        };
    }, []);

    /* Memoized fetch callback.
       If passed to a child component prevent from re-rendering every time the parent is rendered */
    const call = useCallback(
        async <T>(
            url: string,
            options: specialRequestInitCaller,
            isSecured = true,
            forwardError = false
        ): Promise<T | undefined> => {
            didCancel.current = false;

            if (url) {
                setState({ error: undefined, isLoading: true });

                try {
                    const headers = await httpRequestInit(options, isSecured, locale);
                    const response = await tracingFetcherWrapper(fetch)(url, headers);
                    const parsedResponse = (await handleResponse(response, genericErrorMessage)) as T;

                    if (!didCancel.current) {
                        setState({ error: undefined, isLoading: false });

                        return parsedResponse;
                    }
                } catch (apiErrorResponse) {
                    if (!didCancel.current) {
                        const error = getErrorResponseToState(apiErrorResponse, genericErrorMessage);
                        setState({ error, isLoading: false });

                        if (forwardError) {
                            throw error;
                        }
                    }
                }
            }

            return undefined;
        },
        [genericErrorMessage, locale, tracingFetcherWrapper]
    );

    return [
        state,
        {
            get: useCallback(
                <T>(url: string, options?: getSpecialRequestType) =>
                    call<T>(
                        url,
                        { method: "GET", headers: options?.headers },
                        options?.isSecured,
                        options?.forwardError
                    ),
                [call]
            ),
            post: useCallback(
                <T>(url: string, options?: specialRequestType) =>
                    call<T>(
                        url,
                        { method: "POST", headers: options?.headers, body: options?.body },
                        options?.isSecured,
                        options?.forwardError
                    ),
                [call]
            ),
            put: useCallback(
                <T>(url: string, options?: specialRequestType) =>
                    call<T>(
                        url,
                        { method: "PUT", headers: options?.headers, body: options?.body },
                        options?.isSecured,
                        options?.forwardError
                    ),
                [call]
            ),
            patch: useCallback(
                <T>(url: string, options?: specialRequestType) =>
                    call<T>(
                        url,
                        { method: "PATCH", headers: options?.headers, body: options?.body },
                        options?.isSecured,
                        options?.forwardError
                    ),
                [call]
            ),
            deleteCall: useCallback(
                <T>(url: string, options?: specialRequestType) =>
                    call<T>(
                        url,
                        { method: "DELETE", headers: options?.headers, body: options?.body },
                        options?.isSecured,
                        options?.forwardError
                    ),
                [call]
            ),
        },
    ] as const;
};

const genericError = i18n.t("genericError");

export const useFetch = () => useFetchGeneric(genericError);
